import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { removeToken } from '../utils/auth';
import { apiEndpoints } from '../services/api';
import { flushFCMToken, initPushNotifications } from '../services/pushNotifications';
import DriverVehicles from '../pages/DriverVehicles';
import DriverAlerts from '../pages/DriverAlerts';
import DriverEvents from '../pages/DriverEvents';
import DriverAccidents from '../pages/DriverAccidents';
import DriverCosts from '../pages/DriverCosts';

const TABS = [
  {
    id: 'vehicles',
    label: 'Vehicles',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#059669' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 17H3v-5l3.5-6H18l3 6v5h-2"/>
        <circle cx="7.5" cy="17" r="2.5"/>
        <circle cx="16.5" cy="17" r="2.5"/>
        <path d="M10 17h4"/>
      </svg>
    ),
  },
  {
    id: 'alerts',
    label: 'Alerts',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#059669' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  },
  {
    id: 'events',
    label: 'Events',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#059669' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
  },
  {
    id: 'accidents',
    label: 'Accidents',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#059669' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
  },
  {
    id: 'costs',
    label: 'Costs',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? '#059669' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
      </svg>
    ),
  },
];

const DriverShell = () => {
  const { user, logout, pageResetKey, bumpPageReset } = useAppContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('vehicles');

  // Tapping the tab you're already on doesn't change activeTab, so React never
  // re-renders it — force it back to its default (list) view instead.
  const handleTabClick = (id) => {
    if (id === activeTab) bumpPageReset();
    else setActiveTab(id);
  };
  const [reminders, setReminders] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed_reminders') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    apiEndpoints.reminders.list({ date: today, unread: true })
      .then(res => setReminders(res.data || []))
      .catch(() => {});

    // Initialise push notifications after login — permission dialog appears here,
    // not on the login page. flushFCMToken sends any token stored before the JWT
    // was ready.
    flushFCMToken().catch(() => {});
    initPushNotifications().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Show banner when a push notification arrives while the app is open
  useEffect(() => {
    const handlePush = (e) => {
      const n = e.detail || {};
      const data = n.data || {};
      setReminders(prev => [{
        _id: `push-${Date.now()}`,
        message: n.body || data.message || `${data.documentType || 'Document'} for ${data.plate || 'vehicle'} needs attention.`,
        documentType: data.documentType || '',
        plate: data.plate || '',
      }, ...prev]);
    };
    window.addEventListener('fleet:push', handlePush);
    return () => window.removeEventListener('fleet:push', handlePush);
  }, []);

  const visibleReminders = reminders.filter(r => !dismissedIds.includes(r._id));

  const handleDismiss = (id) => {
    apiEndpoints.reminders.markRead(id).catch(() => {});
    const next = [...dismissedIds, id];
    setDismissedIds(next);
    localStorage.setItem('dismissed_reminders', JSON.stringify(next));
  };

  const handleLogout = () => {
    apiEndpoints.auth.logout().catch(() => {}); // best-effort audit log entry, don't block logout on it
    logout();
    removeToken();
    navigate('/driver-login');
  };

  const getInitials = (name) =>
    (name || 'D').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  const mainStyle = { flex: 1, minHeight: 0, padding: '16px', paddingTop: '24px', paddingBottom: '76px', width: '100%', boxSizing: 'border-box', overflowY: 'auto' };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f2f5', fontFamily: "'Plus Jakarta Sans', sans-serif", overflow: 'hidden' }}>

      {/* Header */}
      <header style={{ background: '#0d1117', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/logo-white-fleet.png" alt="Fleet OS" style={{ height: 38, width: 140, objectFit: 'contain', flexShrink: 0 }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff' }}>
              {getInitials(user?.name)}
            </div>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</span>
          </div>
          <button
            onClick={handleLogout}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 7, color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
            </svg>
            Logout
          </button>
        </div>
      </header>

      {/* Reminder banners */}
      {visibleReminders.map(r => (
        <div key={r._id} style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '10px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }}>
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div style={{ flex: 1, fontSize: 13, color: '#92400e' }}>
            <span style={{ fontWeight: 700 }}>Reminder: </span>{r.message || `${r.documentType} document for ${r.plate} needs attention.`}
          </div>
          <button onClick={() => handleDismiss(r._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: 16, lineHeight: 1, flexShrink: 0, padding: '0 2px' }}>✕</button>
        </div>
      ))}

      {/* Page content */}
      <main style={mainStyle}>
        {activeTab === 'vehicles'   && <DriverVehicles key={pageResetKey} />}
        {activeTab === 'alerts'     && <DriverAlerts key={pageResetKey} />}
        {activeTab === 'events'     && <DriverEvents key={pageResetKey} />}
        {activeTab === 'accidents'  && <DriverAccidents key={pageResetKey} />}
        {activeTab === 'costs'      && <DriverCosts key={pageResetKey} />}
      </main>

      {/* Bottom navigation */}
      <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #e5e7eb', display: 'flex', zIndex: 100, boxShadow: '0 -4px 20px rgba(0,0,0,0.07)' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            style={{
              flex: 1, padding: '10px 4px 12px', border: 'none', background: 'transparent',
              color: activeTab === tab.id ? '#059669' : '#9ca3af',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              borderTop: `2px solid ${activeTab === tab.id ? '#059669' : 'transparent'}`,
              transition: 'color 0.15s',
            }}
          >
            {tab.icon(activeTab === tab.id)}
            <span style={{ fontSize: 10, fontWeight: activeTab === tab.id ? 700 : 500, letterSpacing: 0.2 }}>
              {tab.label}
            </span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default DriverShell;
